import { useQuery } from '@tanstack/react-query';
import { fetchExternalExchangeRates } from '../services/api/externalExchangeRate';

/**
 * Phase 105 (Plan 105-09, D-10): read-only external exchange-rate hook,
 * backed by GET /api/external/exchange-rates.
 *
 * v4.0 Landmine #8: the underlying transport
 * (services/api/externalExchangeRate.ts) uses fetch() directly with
 * X-Audience: external — it does NOT route through services/api/client.ts,
 * and does NOT import getExchangeRates from services/exchangeRate.ts.
 *
 * `retry: false` — same rationale as useExternalLeads: a 403 from a revoked
 * grant should surface immediately, not be retried. `staleTime` is generous
 * (24h) because ČNB publishes rates at most once per business day, mirroring
 * the internal page's own `staleTime` on its `exchangeRates` query
 * (pages/sales/LeadsPage.tsx).
 *
 * Fails soft: `select`/`placeholderData` are not used — instead the caller
 * (external LeadsPage) reads `data` directly, which TanStack Query leaves
 * `undefined` on error. That `undefined` is exactly what the FX computation
 * mirrored from the internal page treats as "rates unavailable, fall back
 * to the raw amount" (T-109-FXFAIL) — no error is thrown up to the board.
 */
export function useExternalExchangeRates(date: string) {
  return useQuery<Map<string, number>>({
    queryKey: ['external-exchange-rates', date],
    queryFn: () => fetchExternalExchangeRates(date),
    retry: false,
    staleTime: 24 * 60 * 60 * 1000,
  });
}
